import { useCallback, useRef } from 'react'

import { hasDebugQueryParam } from 'shared/constants/debug_query'

/**
 * Hook that provides debounced debug logging functionality.
 * Only logs when ?debug=true is in the URL, and debounces logs by 1 second to reduce noise.
 *
 * @returns A function that logs debug messages (debounced)
 */
export const useDebouncedDebugLog = () => {
  const debugLogTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const debouncedDebugLog = useCallback((message: string, data: Record<string, unknown>) => {
    if (typeof window === 'undefined') return
    if (!hasDebugQueryParam(window.location.search)) return

    if (debugLogTimeoutRef.current) {
      clearTimeout(debugLogTimeoutRef.current)
    }
    debugLogTimeoutRef.current = setTimeout(() => {
      console.debug(message, data)
      debugLogTimeoutRef.current = null
    }, 1000) // Debounce by 1 second
  }, [])

  return debouncedDebugLog
}
